import { ErrorType } from "@components/global/new/Error/Error";
import { IDaySchedule, IMiningPlanOptions, IPlanRange } from "@services/getMinerList";
import { AccountRequest } from "@services/http/auth";
import { AccountRecord } from "@services/http/responses/AccountResponse";
import { RecordResponse } from "@services/http/responses/BaseResponse";
import useApiRequests from "@services/http/useApiRequest";
import {
  CreateMiningPlanRequest,
  createPlanTemplates,
  deletePlanTemplates,
  getPlanTemplates,
  MiningPlanParamsI,
} from "@services/miningRate";
import { setMiningPlans } from "@store/miningPlans/actions";
import { dev } from "@utils/dev";
//import { getRandomColor } from "@utils/getRandomColor";
import { useAuth } from "@utils/hooks/useAuth";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { useLocale } from "@utils/hooks/useLocale";
import useTypedRequest from "@utils/hooks/useTypedRequest";
import { makeNotifs } from "@utils/makeNotifs";
import moment from "moment";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useDispatch } from "react-redux";
import {
  checkingOverlapPeriods,
  formatWithoutTz,
  getPeriodsAfterSkip,
  parseDateFromFormat,
} from "./helpers";
import { clearEmptyPeriodsWithoutWeekDayPlan, createColorWithCheck, getColorForRange, getKeyForPeriods, getKeyForRange, getNextDay, IPeriodColor, periodsToPlanRange } from "./PlanCreator.service";
import { isEmptyPeriods } from "./Periods.utils";
import { useIntl } from "react-intl";
import { TZ_MSK } from "@utils/moscowTime";
import usePlanTemplates, { IPlanTemplate } from "./context/PlanTemplates.hook";

const DEFAULT_PERIOD = {
  time: ["00:00", "23:59"],
  active: true,
} as IPlanRange;

interface IWeekDayPeriods {
  //readonly isCrossedDay?: boolean;
  readonly dayIndex: string;
  readonly range: IPlanRange[];
}

interface IPlanCreatorContextParams {
  readonly reset?: number;
  readonly plan?: IMiningPlanOptions;
  readonly disabled?: boolean;
  readonly readOnly?: boolean;
  readonly errors?: ErrorType;
  readonly templates: IPlanTemplate[];
  readonly currentDay?: IDaySchedule;
  readonly selectedDays: string[];
  readonly periods: IPlanRange[];
  readonly weekDayPeriods?: IWeekDayPeriods;
  readonly planDays: IDaySchedule[];
  readonly planName: string;
  readonly isAllowAdd: boolean;
  readonly isAllowSkipBefore: boolean;

  readonly initialDate?: Date;

  setPlanName(value: string): void;
  selectDay(day: string, multiple: boolean): void;

  setPlan(value: IMiningPlanOptions | undefined): void;

  setPeriods(value: IPlanRange[]): void; //React.Dispatch<React.SetStateAction<IPlanRange[]>>;
  setPeriodsWithTemplate(value: ITemplateMetaWithColor): void;
  onSavePlan(): void;
  afterSave?(result: IDaySchedule[]): void;
  beforePost?(data: MiningPlanParamsI): void;
  setDisabled(value: boolean): void;

  deleteTemplatesByPlanId(planId: number): void;
  appendTemplates(planId: number, periods: string[][]): void;
  isCorrectPeriods(periods: string[][]): boolean;
  onSelectCurrentDay(date: Date, isEditMode: boolean): void;
  getPlanDayForDate(date: Date): IDaySchedule | undefined;
  updateCurrentDayByDayIndex(dayIndex: string): void;
  getCurrentPlan(): IDaySchedule | undefined;
  doSelectDateAndEdit(date: Date): void;
  addDefaultPeriod(): void;
  isEdited(date: Date): boolean;
  removePeriodByIndex(idx: number): void;
  updatePeriodByIndex(idx: number, value: [string, string]): void;
}

interface IPlanCreatorProviderProps {
  readonly children: React.ReactNode;
  readonly value: Omit<
    IPlanCreatorContextParams,
    | "setDisabled"
    | "setPlan"
    | "setErrors"
    | "templates"
    | "saveTemplate"
    | "applyTemplate"
    | "onAplyTemplate"
    | "onSetCurrentDay"
    | "onSetSelectedDays"
    | "onSavePlan"
    | "periods"
    | "setPeriods"
    | "alreadySavedDays"
    | "planName"
    | "setPlanName"
    | "currentDay"
    | "currentPeriods"
    | "selectedDays"
    | "selectDay"
    | "planDays"
    | "updatePeriod"
    | "setPeriodsWithTemplate"
    | "deleteTemplatesByPlanId"
    | "appendTemplates"
    | "isCorrectPeriods"
    | "onSelectCurrentDay"
    | "getPlanDayForDate"
    | "updateCurrentDayByDayIndex"
    | "weekDayPeriods"
    | "getCurrentPlan"
    | "doSelectDateAndEdit"
    | "addDefaultPeriod"
    | "isEdited"
    | "removePeriodByIndex"
    | "updatePeriodByIndex"
  >;
}

export const PlanCreatorContext =
  React.createContext<IPlanCreatorContextParams>({
    reset: 0,
    templates: [],
    disabled: false,
    readOnly: false,
    periods: [],
    planName: "",
    selectedDays: [],
    planDays: [],
    isAllowAdd: true,
    isAllowSkipBefore: true,
    setPlan() { },
    setPeriods() { },
    selectDay() { },
    setPlanName() { },
    onSavePlan() { },
    setPeriodsWithTemplate() { },
    deleteTemplatesByPlanId() { },
    appendTemplates() { },
    setDisabled() { },
    isCorrectPeriods() {
      return true;
    },
    doSelectDateAndEdit() { },
    updateCurrentDayByDayIndex() { },
    onSelectCurrentDay() {
      dev.logt("PlanCreator", "onSelectCurrentDay");
    },
    getPlanDayForDate() {
      return undefined;
    },
    getCurrentPlan() {
      return undefined;
    },
    addDefaultPeriod() { },
    isEdited(date: Date) { return false; },
    removePeriodByIndex() { },
    updatePeriodByIndex(idx: number, value: [string, string]) { },
  });



export interface ITemplateMetaWithColor {
  color: string;
  periods: [string, string][];
}

export function PlanCreatorProvider({
  children,
  value,
}: IPlanCreatorProviderProps) {

  const { locale } = useLocale();
  const weekDays = moment.localeData(locale).weekdays();

  const { isAdmin } = useAuth();
  const { onSuccess, onLoader } = useGlobalNotifs();
  const { enqueue } = useApiRequests();
  const notifs = makeNotifs();
  const dispatch = useDispatch();

  const initialPlan = useRef<IMiningPlanOptions>();

  const [disabled, setDisabled] = useState<boolean>(value.disabled !== undefined ? value.disabled : true);

  const [plan, setPlan] = useState<IMiningPlanOptions | undefined>();
  const [planName, setPlanName] = useState<string>(value.plan?.name || "");
  const [planDays, setPlanDays] = useState<IDaySchedule[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [periods, setPeriods] = useState<IPlanRange[]>([]);
  const [editedDays, setEditedDays] = useState<IDaySchedule[]>([]);

  useEffect(() => {
    setPlan(value.plan);
  }, [value.plan]);

  // выбранные день, конкретный, отображаемый в данный момент, может быть частью selectedDays
  const [currentDay, setCurrentDay] = useState<IDaySchedule>();
  const [weekDayPeriods, setWeekDayPeriods] = useState<IWeekDayPeriods>();
  // в selectedDays хранятся редактируемые дни

  const [errors, setErrors] = useState<ErrorType>();
  const [periodsColors, setPeriodsColors] = useState<IPeriodColor[]>([]);

  const getColorForPeriods = useCallback((period: [string, string][]) => {
    for (let i = 0; i < periodsColors.length; i++) {
      if (getKeyForPeriods(period) === periodsColors[i].key) {
        return periodsColors[i].color
      }
    }
    return createColorWithCheck(periodsColors.map(pc => pc.color));
  }, [periodsColors]);

  const updateColorForRange = useCallback((range: IPlanRange[]): IPeriodColor => {
    if (range.length === 0) {
      return { key: "", color: "" };
    }
    const newColor = getColorForRange(range, periodsColors);
    if (newColor.key === "new") {
      periodsColors.push({
        ...newColor,
        key: getKeyForRange(range),
      });
      setPeriodsColors([...periodsColors]);
    }
    return newColor;
  }, [periodsColors, setPeriodsColors]);

  const {
    templates,
    loadTemplates,
    deleteTemplatesByPlanId,
    appendTemplates
  } = usePlanTemplates(getColorForPeriods);

  const addDefaultPeriod = useCallback(() => {
    if (selectedDays.length === 0) return;
    const values = [...periods, DEFAULT_PERIOD];
    const color = updateColorForRange(values).color;
    selectedDays.forEach(day => {
      let exists = editedDays.find(ed => ed.day === day);
      if (!exists) {
        setEditedDays((prev) => [...prev, {
          day,
          color,
          range: values
        }]);
      } else {
        setEditedDays(editedDays.map(ed => {
          if (ed.day === day) {
            return {
              ...ed,
              color,
              range: values
            };
          }
          return ed;
        }));
      }
    });
  }, [selectedDays, periods, editedDays, setEditedDays, updateColorForRange]);

  const updatePeriodByIndex = useCallback((idx: number, value: [string, string]) => {
    if (selectedDays.length === 0) return;
    const values = periods.map((item, i) => {
      if (i === idx) {
        return {
          ...item,
          time: value
        } as IPlanRange;
      } else {
        return item;
      }
    });
    const color = updateColorForRange(values).color;
    selectedDays.forEach(day => {
      const exists = editedDays.find(ed => ed.day === day);
      if (!exists) {
        setEditedDays((prev) => [...prev, {
          day,
          color,
          range: values
        }]);
      } else {
        // не очень оптимально (цикл в цикле), но пока так
        setEditedDays(prev => prev.map(ed => {
          if (ed.day === day) {
            return {
              ...ed,
              color,
              range: values
            };
          } else {
            return ed;
          }
        }));
      }
    });
  }, [selectedDays, periods, editedDays, setEditedDays, updateColorForRange]);

  const removePeriodByIndex = useCallback((idx: number) => {
    if (selectedDays.length === 0) return;
    const values = periods.filter((_, i) => i !== idx);
    const color = updateColorForRange(values).color;
    selectedDays.forEach(day => {
      const exists = editedDays.find(ed => ed.day === day);
      if (!exists) {
        setEditedDays((prev) => [...prev, {
          day,
          color,
          range: values
        }]);
      } else {
        setEditedDays(editedDays.map(ed => {
          if (ed.day === day) {
            return {
              ...ed,
              color,
              range: values
            };
          } else {
            return ed;
          }
        }));
      }
    });
  }, [selectedDays, periods, editedDays, setEditedDays, updateColorForRange]);

  const isEdited = useCallback((date: Date): boolean => {
    const day = formatWithoutTz(date);
    return editedDays.findIndex(ed => ed.day === day || ed.day === date.getDay().toString()) !== -1;
  }, [editedDays]);

  const isAllowAdd = useMemo(() => {
    if (periods.length === 0) return true;
    if (periods[periods.length - 1].time[1] === "") return false;
    return true;
  }, [periods]);

  const isAllowSkipBefore = useMemo(() => {
    if (periods.length > 0) return true;
    return false;
  }, [periods]);

  // пока оставлю, может пригодиться для отладки
  // useEffect(() => {
  //   dev.logt("Plans", "Periods", { periods, isAllowAdd });
  // }, [periods, isAllowAdd]);

  // useEffect(() => {
  //   dev.logt("Plans", "isAllowAdd", isAllowAdd);
  // }, [isAllowAdd]);

  const updateCurrentDayByDayIndex = (dayIndex: string) => {
    const exists = planDays.find(pd => pd.day === dayIndex);
    if (exists) {
      setCurrentDay({ ...exists });
    } else {
      setCurrentDay({
        day: dayIndex,
        color: "",
        range: [],
      });
    }
  }

  const onSelectCurrentDay = useCallback((
    date: Date,
    isEditMode: boolean
  ) => {
    if (isEditMode) {
      const exists = planDays.find(pd => pd.day === formatWithoutTz(date));
      if (exists) {
        setCurrentDay({ ...exists, day: formatWithoutTz(date) });
      } else {
        setCurrentDay({
          day: formatWithoutTz(date),
          color: "",
          range: [],
        });
      }
      return;
    }

    const plan = getPlanDayForDate(date);

    if (typeof plan === "undefined") {
      setCurrentDay({
        day: formatWithoutTz(date),
        color: "",
        range: [],
      });
    } else {
      setCurrentDay({ ...plan, day: formatWithoutTz(date) });
    }
  },
    [planDays]
  );

  const selectDay = useCallback((
    day: string,
    multiple: boolean
  ) => {
    lastSelectionRef.current = {
      day,
      color: "", //newColor,
      range: [] //unused 
    } as IDaySchedule;

    if (multiple) {
      setSelectedDays((prev) => {
        if (prev.indexOf(day) === -1) {
          return [...prev, day];
        }
        return prev.filter((d) => d !== day);
      });
    } else {
      if (selectedDays.findIndex((v) => v === day) !== -1) {
        setSelectedDays([]);
      } else {
        setSelectedDays([day]);
      }
    }

  }, [selectedDays, planDays, setSelectedDays]);

  const selectSelection = useCallback(() => {
    if (currentDay) { // если есть, то это всегда DD/MM/YYYY, т.к. заарнее день недели выбрать нельзя
      selectDay(currentDay.day, false);
    } else {
      const now = new Date();
      const ddmmyyyy = formatWithoutTz(now);
      selectDay(ddmmyyyy, false);
      const currDay = planDays.find(
        (dd) => dd.day === ddmmyyyy || dd.day === now.getDay().toString()
      );
      if (currDay) {
        setCurrentDay({ ...currDay, day: ddmmyyyy });
      } else {
        setCurrentDay({
          day: ddmmyyyy,
          color: "",
          range: [],
        });
      }
    }
  }, [currentDay, planDays, selectDay]);

  const doSelectDateAndEdit = useCallback((date: Date) => {
    const day = formatWithoutTz(date);
    setDisabled(false);
    const exists = planDays.find(pd => pd.day === day);
    if (exists) {
      setCurrentDay({ ...exists, day: formatWithoutTz(date) });
    } else {
      setCurrentDay({
        day: formatWithoutTz(date),
        color: "",
        range: [],
      });
    }
    setDisabled(false);
  }, [planDays]);

  const isCorrectPeriods = (values: [string, string][]): boolean => {
    setErrors(undefined);
    if (checkingOverlapPeriods(values)) {
      setErrors({ ownError: [notifs.errors.miner_plan__periods] });
      return false;
    }
    return true;
  };

  const onSavePlan = useCallback(async () => {
    const editedDaysCache = [...editedDays];
    // если выбрали несколько дней, но не редактировали периоды, то заменить текущим у всех выбранных
    selectedDays.forEach(day => {
      const exists = editedDaysCache.find(ed => ed.day === day);
      if (!exists) {
        editedDaysCache.push({
          day,
          color: getColorForRange(periods, periodsColors).color,
          range: [...periods]
        });
      } else {
        editedDaysCache.map(ed => {
          if (ed.day === day) {
            return {
              ...ed,
              color: getColorForRange(periods, periodsColors).color,
              range: [...periods]
            };
          }
          return ed;
        });
      }
    });
    if (editedDaysCache.length === 0) return;
    const newSchedule = editedDaysCache.map((item) => {
      if (item.range.length === 0) {
        return {
          ...item,
          color: ""
        }
      }
      return {
        ...item,
      }
    });

    const oldSchedule = planDays.filter((item) => {
      return !editedDaysCache.some(x => x.day === item.day); //notExists;
    });

    const params: MiningPlanParamsI = {
      ...plan,
      name: planName,
      schedule: clearEmptyPeriodsWithoutWeekDayPlan([...oldSchedule, ...newSchedule]),
      edit: !!plan?.id,
      id: !!plan?.id ? plan.id : undefined,
      meta: plan?.meta ?? ""
    };

    value.beforePost?.(params);
    const respone = await enqueue(new CreateMiningPlanRequest(params), {
      onLoadingStatusChanged: onLoader,
    });
    if (!respone) return;
    updatePlans();
    onSuccess(notifs.ok.success);
    value.afterSave?.(planDays);
    setDisabled(true);
  }, [editedDays, plan, planDays, planName, periods, selectedDays, templates, periodsColors, currentDay]);

  const updatePlans = async () => {
    const response = await enqueue<RecordResponse<AccountRecord>>(
      new AccountRequest({})
    );
    if (!response) return;
    dispatch(setMiningPlans(response.Record.plans || []));
  };

  const setPeriodsWithTemplate = (values: ITemplateMetaWithColor) => {
    setPeriods(periodsToPlanRange(values.periods));
  };


  const getPlanDayForDate = useCallback(
    (date: Date) => {
      // у даты приоритет над днем недели
      let result = editedDays.find(ed => ed.day === formatWithoutTz(date));
      if (!result) {
        result = editedDays.find(ed => ed.day === date.getDay().toString());
      }
      if (!result) {
        result = planDays.find(
          (dd) => dd.day === formatWithoutTz(date)
        );
        if (!result) {
          result = planDays.find((dd) => dd.day === date.getDay().toString());
        }
      }
      return result;
    },
    [planDays, editedDays]
  );

  const getCurrentPlan = useCallback(() => {
    if (!currentDay) return undefined;
    let plan = planDays.find(pd => pd.day === currentDay.day);
    if (plan) {
      if (!isEmptyPeriods(plan.range)) {
        return plan;
      }
    }
    const planDate = parseDateFromFormat(currentDay.day);
    plan = planDays.find(pd => pd.day === planDate.getDay().toString());
    if (plan) return plan;
    return undefined;
  }, [currentDay, planDays]);

  useEffect(() => {
    if (!isAdmin) return;
    loadTemplates();
  }, []);

  useEffect(() => {
    initialPlan.current = plan;
    setPlanName(plan?.name || "");
    setPlanDays([...(plan?.schedule || [])]);
  }, [plan, value.reset]);

  useEffect(() => {
    const periodsWithColor: IPeriodColor[] = [];
    (plan?.schedule || []).forEach(item => {
      periodsWithColor.push({
        key: item.range.map(r => r.time.join('-')).join(','),
        color: item.color
      })
    });
    templates.forEach(t => {
      periodsWithColor.push({
        key: t.times.map(r => r.join('-')).join(','),
        color: t.color
      })
    })
    setPeriodsColors(periodsWithColor);
  }, [plan, templates]);

  const getEdited = useCallback((day: string): IDaySchedule | undefined => {
    return editedDays.find(ed => ed.day === day);
  }, [editedDays]);


  useEffect(() => {
    if (currentDay === undefined) {
      setPeriods([]);
    } else {
      const edited = getEdited(currentDay.day);
      if (edited) {
        setPeriods(edited.range);
      } else {
        let currentPlan = planDays.find(pd => pd.day === currentDay.day);
        if (currentPlan) {
          setPeriods(currentPlan.range);
        } else {
          setPeriods([]);
        }
      }
    }
    if (currentDay && currentDay.day.length === "DD/MM/YYYY".length) {
      const date = parseDateFromFormat(currentDay.day);
      let weekDayPlan = planDays.find(pd => pd.day === date.getDay().toString());
      if (weekDayPlan) {
        setWeekDayPeriods({
          dayIndex: weekDays[date.getDay()],
          range: weekDayPlan.range
        });
      } else {
        setWeekDayPeriods(undefined);
      }
    } else {
      setWeekDayPeriods(undefined);
    }
  }, [currentDay, planDays, getEdited, editedDays]);

  useEffect(() => {
    if (disabled) {
      setEditedDays([]);
      setSelectedDays([]);
      setErrors(undefined);
    } else {
      selectSelection();
    }
  }, [disabled]);

  const lastSelectionRef = useRef<IDaySchedule>();

  useEffect(() => {
    lastSelectionRef.current = currentDay;
  }, [currentDay]);

  useEffect(() => {
    if (lastSelectionRef.current !== undefined) return;
    const now = moment.tz(new Date(), TZ_MSK); // тут вопрос...
    const ddmmyyyy = now.format("DD/MM/YYYY");
    const currDay = planDays.find(
      (dd) => dd.day === ddmmyyyy || dd.day === now.day().toString()
    );
    if (currDay) {
      setCurrentDay({ ...currDay, day: ddmmyyyy });
    } else {
      setCurrentDay({
        day: ddmmyyyy,
        color: "",
        range: [],
      });
    }
  }, [planDays, selectedDays]);

  return (
    <PlanCreatorContext.Provider
      value={{
        ...value,
        currentDay,
        templates,
        errors,
        planName,
        selectedDays,
        periods,
        planDays,
        weekDayPeriods,
        isAllowAdd,
        isAllowSkipBefore,
        plan,
        disabled,
        setDisabled,
        setPlan,
        setPeriods,
        selectDay,
        setPlanName,
        onSavePlan,
        setPeriodsWithTemplate,
        deleteTemplatesByPlanId,
        appendTemplates,
        isCorrectPeriods,
        onSelectCurrentDay,
        getPlanDayForDate,
        updateCurrentDayByDayIndex,
        getCurrentPlan,
        doSelectDateAndEdit,
        addDefaultPeriod,
        isEdited,
        removePeriodByIndex,
        updatePeriodByIndex,
      }}
    >
      {children}
    </PlanCreatorContext.Provider>
  );
}

export const usePlanCreatorContext = () => useContext(PlanCreatorContext);
